import { KeyboardEvent, MouseEvent, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { isValidOnboardingKey } from "@src/cloud-connect/utils/is-valid-onboarding-key";
import {
  errorSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  AnnouncementStatus,
  GatewayOperationError,
  GatewayOnboardingStatus,
} from "@src/types/gateway-onboarding";
import {
  resetOnboardingState,
  setAnnouncementStatus,
  setGatewayDetails,
  setGatewayOnboardingStatus,
} from "@src/reducers/onboarding-slice";
import { RootState } from "@src/reducers/store";
import { gatewayIsAnnouncedUseCase } from "@src/cloud-connect/client-configuration/use-cases/gateway-is-announced-use-case";
import { gatewayOnboardOnlyUseCase } from "@src/cloud-connect/client-configuration/use-cases/gateway-onboard-only-use-case";
import { Help } from "@src/cloud-connect/help/entities/help";
import { HelpRepository } from "@src/cloud-connect/help/repositories/help-repository";
import { updateModalsCloseDrawerModal } from "@src/reducers/your-reducer";
import navigateTo from "@src/routes/navigator";
import {
  getGatewayDrawerTexts,
  getGatewayOnboardingSteps,
  getGatewayTargetRoute,
  isGatewayOnboardingCompletedStatus,
  pollGatewayConnection,
  pollOnboardingOperation,
  resetGatewayLocalState,
} from "./use-add-new-gateway-drawer-modal-helpers";

export const useAddNewGatewayDrawerModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [onboardingKey, setOnboardingKey] = useState("");
  const [isConnectGatewayLoading, setIsConnectGatewayLoading] = useState(false);
  const [operationFailureReason, setOperationFailureReason] = useState("");
  const [operationFailureDetails, setOperationFailureDetails] =
    useState<GatewayOperationError | null>(null);
  const [showProtocolDetails, setShowProtocolDetails] = useState(false);
  const [isGatewayAnnounced, setIsGatewayAnnounced] = useState(false);
  const [isGatewayAlreadyOnboarded, setIsGatewayAlreadyOnboarded] =
    useState(false);
  const [isGatewayConnectionReady, setIsGatewayConnectionReady] =
    useState(false);
  const [pendingGatewayOperation, setPendingGatewayOperation] = useState<{
    operationId: string;
    gatewayId: string;
  } | null>(null);
  const lastGatewayOperationRef = useRef<{
    operationId: string;
    gatewayId: string;
  } | null>(null);
  const gatewayFlowGenerationRef = useRef(0);
  const gatewayOperationAbortControllerRef = useRef<AbortController | null>(
    null,
  );

  const gatewayOnboardingStatus = useSelector(
    (state: RootState) => state.onboardingSlice.gatewayOnboardingStatus,
  );
  const announcementStatus = useSelector(
    (state: RootState) => state.onboardingSlice.announcementStatus,
  );
  const gatewayDetails = useSelector(
    (state: RootState) => state.onboardingSlice.onboardingDetails.gateway,
  );

  const texts = getGatewayDrawerTexts(i18n);

  const isGatewayOnboardingCompleted = isGatewayOnboardingCompletedStatus(
    gatewayOnboardingStatus,
  );

  const steps = getGatewayOnboardingSteps(isGatewayOnboardingCompleted, texts);
  const isGatewayInputDisabled =
    announcementStatus === AnnouncementStatus.LOADING ||
    announcementStatus === AnnouncementStatus.ANNOUNCED ||
    announcementStatus === AnnouncementStatus.SUCCESS ||
    announcementStatus === AnnouncementStatus.WARNING ||
    announcementStatus === AnnouncementStatus.FAILED;

  const showGatewayConnectButton =
    announcementStatus === AnnouncementStatus.IDLE &&
    gatewayOnboardingStatus === GatewayOnboardingStatus.GATEWAY_NOT_STARTED;

  const showFinishOnboardingButtons =
    announcementStatus === AnnouncementStatus.LOADING ||
    announcementStatus === AnnouncementStatus.ANNOUNCED ||
    announcementStatus === AnnouncementStatus.SUCCESS;

  const isFinishOnboardingButtonEnabled =
    announcementStatus === AnnouncementStatus.SUCCESS &&
    gatewayOnboardingStatus ===
      GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED;
  const isContinueMachineOnboardingEnabled =
    isFinishOnboardingButtonEnabled && isGatewayConnectionReady;

  const handleConnectGatewayClick = async (
    e: MouseEvent | KeyboardEvent,
  ): Promise<void> => {
    e.preventDefault();

    if (!isValidOnboardingKey(onboardingKey)) {
      warningSnackbar(texts.invalidOnboardingKeyErrorMessage, 5000);
      return;
    }

    const trimmedKey = onboardingKey.trim();
    gatewayOperationAbortControllerRef.current?.abort();
    const flowGeneration = ++gatewayFlowGenerationRef.current;
    const isCurrentFlow = () =>
      flowGeneration === gatewayFlowGenerationRef.current;

    setIsConnectGatewayLoading(true);

    dispatch(
      setGatewayOnboardingStatus(
        GatewayOnboardingStatus.GATEWAY_ONBOARDING_IN_PROGRESS,
      ),
    );
    dispatch(setAnnouncementStatus(AnnouncementStatus.LOADING));

    const markGatewayOnboardingFailed = (
      reason?: string,
      details?: GatewayOperationError,
    ) => {
      setOperationFailureReason(reason || "");
      setOperationFailureDetails(details || null);
      dispatch(
        setGatewayOnboardingStatus(
          GatewayOnboardingStatus.GATEWAY_ONBOARDING_FAILED,
        ),
      );
      dispatch(setAnnouncementStatus(AnnouncementStatus.FAILED));
    };

    setPendingGatewayOperation(null);
    lastGatewayOperationRef.current = null;
    setOperationFailureReason("");
    setOperationFailureDetails(null);
    setShowProtocolDetails(false);
    setIsGatewayAnnounced(false);
    setIsGatewayAlreadyOnboarded(false);
    setIsGatewayConnectionReady(false);

    try {
      const announceResult = await gatewayIsAnnouncedUseCase(trimmedKey);

      if (!isCurrentFlow()) return;

      if (!announceResult.success) {
        markGatewayOnboardingFailed();
        return;
      }

      if (announceResult.isOnboarded) {
        setIsGatewayAlreadyOnboarded(true);
        markGatewayOnboardingFailed();
        return;
      }

      if (!announceResult.isAnnounced) {
        errorSnackbar(texts.announceFailed);
        markGatewayOnboardingFailed();
        return;
      }

      dispatch(
        setGatewayOnboardingStatus(
          GatewayOnboardingStatus.GATEWAY_ONBOARDING_ANNOUNCED,
        ),
      );
      setIsGatewayAnnounced(true);
      dispatch(setAnnouncementStatus(AnnouncementStatus.ANNOUNCED));

      const onboardResult = await gatewayOnboardOnlyUseCase(trimmedKey);

      if (!isCurrentFlow()) return;

      if (!onboardResult.success) {
        markGatewayOnboardingFailed();
        return;
      }

      const operationId = onboardResult.data?.operation_id;
      const gatewayId = onboardResult.data?.gateway_id;

      if (!operationId || !gatewayId) {
        markGatewayOnboardingFailed();
        return;
      }

      setPendingGatewayOperation({ operationId, gatewayId });
      lastGatewayOperationRef.current = { operationId, gatewayId };
      dispatch(setGatewayDetails({ gateway_id: gatewayId }));
      const operationAbortController = new AbortController();
      gatewayOperationAbortControllerRef.current = operationAbortController;
      const operationStatusResult = await pollOnboardingOperation(
        operationId,
        operationAbortController.signal,
      );

      if (!isCurrentFlow()) return;

      if (operationStatusResult.status === "error") {
        setPendingGatewayOperation(null);
        markGatewayOnboardingFailed(
          operationStatusResult.reason,
          operationStatusResult.details,
        );
        return;
      }

      if (operationStatusResult.status === "pending") {
        dispatch(
          setGatewayOnboardingStatus(
            GatewayOnboardingStatus.GATEWAY_ONBOARDING_IN_PROGRESS,
          ),
        );
        dispatch(setAnnouncementStatus(AnnouncementStatus.WARNING));
        return;
      }

      setPendingGatewayOperation(null);
      dispatch(
        setGatewayOnboardingStatus(
          GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED,
        ),
      );
      dispatch(setAnnouncementStatus(AnnouncementStatus.SUCCESS));

      const gatewayDetailsResult = await pollGatewayConnection(
        gatewayId,
        operationAbortController.signal,
      );

      if (!isCurrentFlow()) return;

      const isGatewayConnectionReadyAfterPolling =
        (gatewayDetailsResult?.connection_counter || 0) > 0;
      if (gatewayDetailsResult) {
        dispatch(
          setGatewayDetails({
            ...gatewayDetailsResult,
            gateway_id: gatewayDetailsResult.gateway_id || gatewayId,
          }),
        );
      }
      setIsGatewayConnectionReady(isGatewayConnectionReadyAfterPolling);
      if (!isGatewayConnectionReadyAfterPolling) {
        markGatewayOnboardingFailed(texts.gatewayConnectionNotEstablished);
      }
    } catch {
      if (!isCurrentFlow()) return;
      setPendingGatewayOperation(null);
      markGatewayOnboardingFailed();
    } finally {
      if (isCurrentFlow()) {
        setIsConnectGatewayLoading(false);
      }
    }
  };

  const handleRetry = async (): Promise<void> => {
    gatewayOperationAbortControllerRef.current?.abort();
    const flowGeneration = ++gatewayFlowGenerationRef.current;
    const isCurrentFlow = () =>
      flowGeneration === gatewayFlowGenerationRef.current;

    if (pendingGatewayOperation) {
      setIsGatewayConnectionReady(false);
      const operationAbortController = new AbortController();
      gatewayOperationAbortControllerRef.current = operationAbortController;
      try {
        setIsConnectGatewayLoading(true);
        const operationStatusResult = await pollOnboardingOperation(
          pendingGatewayOperation.operationId,
          operationAbortController.signal,
        );

        if (!isCurrentFlow()) return;

        if (operationStatusResult.status === "pending") {
          dispatch(setAnnouncementStatus(AnnouncementStatus.WARNING));
          setIsConnectGatewayLoading(false);
          return;
        }

        if (operationStatusResult.status === "error") {
          setPendingGatewayOperation(null);
          setOperationFailureReason(operationStatusResult.reason || "");
          setOperationFailureDetails(operationStatusResult.details || null);
          dispatch(
            setGatewayOnboardingStatus(
              GatewayOnboardingStatus.GATEWAY_ONBOARDING_FAILED,
            ),
          );
          dispatch(setAnnouncementStatus(AnnouncementStatus.FAILED));
          setIsConnectGatewayLoading(false);
          return;
        }

        dispatch(
          setGatewayOnboardingStatus(
            GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED,
          ),
        );
        dispatch(setAnnouncementStatus(AnnouncementStatus.SUCCESS));

        const gatewayDetailsResult = await pollGatewayConnection(
          pendingGatewayOperation.gatewayId,
          operationAbortController.signal,
        );

        if (!isCurrentFlow()) return;

        setPendingGatewayOperation(null);
        const isGatewayConnectionReadyAfterPolling =
          (gatewayDetailsResult?.connection_counter || 0) > 0;
        if (gatewayDetailsResult) {
          dispatch(
            setGatewayDetails({
              ...gatewayDetailsResult,
              gateway_id:
                gatewayDetailsResult.gateway_id ||
                pendingGatewayOperation.gatewayId,
            }),
          );
        }
        setIsGatewayConnectionReady(isGatewayConnectionReadyAfterPolling);
        if (!isGatewayConnectionReadyAfterPolling) {
          setOperationFailureReason(texts.gatewayConnectionNotEstablished);
          setOperationFailureDetails(null);
          dispatch(
            setGatewayOnboardingStatus(
              GatewayOnboardingStatus.GATEWAY_ONBOARDING_FAILED,
            ),
          );
          dispatch(setAnnouncementStatus(AnnouncementStatus.FAILED));
        }
        setIsConnectGatewayLoading(false);
        return;
      } catch {
        if (!isCurrentFlow()) return;
        setPendingGatewayOperation(null);
        setOperationFailureReason("");
        setOperationFailureDetails(null);
        dispatch(
          setGatewayOnboardingStatus(
            GatewayOnboardingStatus.GATEWAY_ONBOARDING_FAILED,
          ),
        );
        dispatch(setAnnouncementStatus(AnnouncementStatus.FAILED));
        setIsConnectGatewayLoading(false);
        return;
      }
    }

    dispatch(resetOnboardingState());
    setIsGatewayAlreadyOnboarded(false);
    resetGatewayLocalState({
      setOnboardingKey,
      setIsConnectGatewayLoading,
      setOperationFailureReason,
      setOperationFailureDetails,
      setShowProtocolDetails,
      setIsGatewayAnnounced,
    });
  };

  const handleShowProtocol = () => {
    setShowProtocolDetails((currentValue) => !currentValue);
  };

  const handleGetSupport = () => {
    const help = new Help(new HelpRepository());
    window.location.href = help.getContactUrlForFailedGatewayOnboarding(
      "",
      undefined,
      {
        gatewayName: gatewayDetails?.name,
        gatewayUuid:
          gatewayDetails?.gateway_id ||
          pendingGatewayOperation?.gatewayId ||
          lastGatewayOperationRef.current?.gatewayId,
        namespaceName: gatewayDetails?.namespace,
        operationId:
          pendingGatewayOperation?.operationId ||
          lastGatewayOperationRef.current?.operationId,
        failureReason: operationFailureReason,
        failureStage: "Gateway onboarding",
        errorDetails: operationFailureDetails
          ? {
              "Error code": operationFailureDetails.error_code,
              Title: operationFailureDetails.title,
              Detail: operationFailureDetails.detail,
              Severity: operationFailureDetails.severity,
              "Recommended action": operationFailureDetails.recommended_action,
              "Trace ID": operationFailureDetails.trace_id,
            }
          : undefined,
      },
    );
  };

  const handleFinishAndGoToGateway = (e?: MouseEvent | KeyboardEvent): void => {
    e?.preventDefault();
    gatewayOperationAbortControllerRef.current?.abort();
    gatewayFlowGenerationRef.current += 1;
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "addNewGatewayDrawerModal",
      }),
    );
    dispatch(resetOnboardingState());

    navigateTo(getGatewayTargetRoute(gatewayDetails?.gateway_id));
  };

  const handleContinueMachineOnboarding = (): void => {
    gatewayOperationAbortControllerRef.current?.abort();
    gatewayFlowGenerationRef.current += 1;
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "addNewGatewayDrawerModal",
      }),
    );
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "addNewMachineDrawerModal",
      }),
    );
  };

  const handleCloseGatewayDrawer = (): void => {
    gatewayOperationAbortControllerRef.current?.abort();
    gatewayFlowGenerationRef.current += 1;
    setPendingGatewayOperation(null);
    setIsGatewayConnectionReady(false);
    setIsGatewayAlreadyOnboarded(false);
    dispatch(resetOnboardingState());
    resetGatewayLocalState({
      setOnboardingKey,
      setIsConnectGatewayLoading,
      setOperationFailureReason,
      setOperationFailureDetails,
      setShowProtocolDetails,
      setIsGatewayAnnounced,
    });
  };

  return {
    texts,
    onboardingKey,
    setOnboardingKey,
    isGatewayInputDisabled,
    isConnectGatewayLoading,
    showGatewayConnectButton,
    showFinishOnboardingButtons,
    isFinishOnboardingButtonEnabled,
    isContinueMachineOnboardingEnabled,
    announcementStatus,
    gatewayOnboardingStatus,
    isGatewayOnboardingCompleted,
    steps,
    gatewayDetails,
    operationFailureReason,
    operationFailureDetails,
    showProtocolDetails,
    isGatewayAnnounced,
    isGatewayAlreadyOnboarded,
    handleConnectGatewayClick,
    handleRetry,
    handleShowProtocol,
    handleGetSupport,
    handleFinishAndGoToGateway,
    handleContinueMachineOnboarding,
    handleCloseGatewayDrawer,
  };
};
